/**
 * Shared helpers for the PlayEngine global asset-map system.
 *
 * The asset map lives at `playEngineConfig.assets` and is keyed by a short
 * random asset key. Each entry describes one shared asset:
 *
 *   assets[assetKey] = {
 *     type,                       // image | audio | video | font | atlas | bitmap-font
 *     bucket,                     // grouping label (filters dropdowns)
 *     name,                       // optional legacy display label (prefer editing assetKey)
 *     sourceRef,                  // "@server:<mongoId>" (string) or { image, json|xml }
 *     source,                     // persisted as the @server ref; hydrated to base64 at load/build
 *   }
 *
 * The map key (`assetKey`) is the canonical identifier. Components bind via
 * `dependency.assetKey`. The optional `name` field is not used for wiring.
 */

import _ from "lodash";
import { getServerUri } from "./uriUtility";

/** File picker accept string for the font → bitmap-font auto-generate flow. */
export const BITMAP_FONT_SOURCE_ACCEPT = ".ttf,.otf,.woff,.woff2";

/**
 * Generate a readable, collision-resistant asset key shaped as
 * `{type}-{number}` (e.g. "image-372", "bitmap-font-1841"). The number is
 * 3–4 digits. Pass the existing keys so the result is unique within the version.
 *
 * @param {string} [type] asset type used as the prefix (defaults to "asset")
 * @param {Set<string>|string[]} [existingKeys] keys to avoid colliding with
 */
export function genAssetKey(type = "asset", existingKeys = []) {
  const prefix = String(type || "asset");
  const taken = existingKeys instanceof Set ? existingKeys : new Set(existingKeys || []);
  for (let attempt = 0; attempt < 60; attempt++) {
    const digits = Math.random() < 0.5 ? 3 : 4;
    const min = 10 ** (digits - 1);
    const num = Math.floor(min + Math.random() * (9 * min));
    const key = `${prefix}-${num}`;
    if (!taken.has(key)) return key;
  }
  // Extremely unlikely fallback — guarantee uniqueness with extra entropy.
  return `${prefix}-${Date.now().toString().slice(-5)}`;
}

/** Trim and normalise a user-supplied asset key. */
export function normalizeAssetKey(input) {
  return String(input ?? "").trim();
}

/**
 * Validate an asset-map key. Returns an error string, or null when valid.
 * @param {string} key
 * @param {Set<string>|string[]} existingKeys
 * @param {string} [excludeKey] - allow keeping the same key when re-validating
 */
export function validateAssetKey(key, existingKeys, excludeKey = null) {
  const normalized = normalizeAssetKey(key);
  if (!normalized) return "Key cannot be empty";
  if (normalized.length > 64) return "Key must be 64 characters or fewer";
  if (!/^[a-zA-Z0-9_-]+$/.test(normalized)) {
    return "Use letters, numbers, underscore, or hyphen only";
  }
  const taken = existingKeys instanceof Set
    ? existingKeys.has(normalized)
    : (existingKeys || []).includes(normalized);
  if (taken && normalized !== excludeKey) return "That key is already in use";
  return null;
}

/**
 * List every component dependency and localization override bound to an asset key.
 * @returns {Array<{ kind: 'dep'|'override', componentId: string, depKey: string, language?: string }>}
 */
export function collectAssetKeyUsages(config, assetKey) {
  const usages = [];
  for (const comp of config?.components || []) {
    for (const dep of comp.dependencies || []) {
      if (dep?.assetKey === assetKey) {
        usages.push({ kind: "dep", componentId: comp.id, depKey: dep.key });
      }
    }
  }
  const overrides = _.get(config, "localization.overrides") || {};
  for (const [language, langOverrides] of Object.entries(overrides)) {
    for (const [componentId, compOverride] of Object.entries(langOverrides || {})) {
      for (const [depKey, value] of Object.entries(compOverride?.dependencyOverrides || {})) {
        if (value === assetKey) {
          usages.push({ kind: "override", language, componentId, depKey });
        }
      }
    }
  }
  return usages;
}

/**
 * Rename an asset-map key inside a playEngineConfig (mutates `config`).
 * Updates every dependency.assetKey and localization override string.
 */
export function rekeyAssetInConfig(config, oldKey, newKey) {
  const normalized = normalizeAssetKey(newKey);
  if (!normalized || normalized === oldKey) return normalized;

  const entry = config.assets?.[oldKey];
  if (!entry) throw new Error(`Asset "${oldKey}" not found`);
  if (!config.assets || Array.isArray(config.assets)) config.assets = {};

  const existing = new Set(Object.keys(config.assets));
  const err = validateAssetKey(normalized, existing, oldKey);
  if (err) throw new Error(err);

  config.assets[normalized] = entry;
  delete config.assets[oldKey];

  for (const usage of collectAssetKeyUsages(config, oldKey)) {
    if (usage.kind === "dep") {
      const comp = (config.components || []).find((c) => c.id === usage.componentId);
      const dep = comp?.dependencies?.find((d) => d.key === usage.depKey);
      if (dep) dep.assetKey = normalized;
    } else {
      const dov = _.get(
        config,
        `localization.overrides.${usage.language}.${usage.componentId}.dependencyOverrides`
      );
      if (dov && dov[usage.depKey] === oldKey) dov[usage.depKey] = normalized;
    }
  }

  return normalized;
}

/**
 * Remove an asset-map entry and unbind every dependency / localization override
 * that references it. Mutates `config`.
 */
export function removeAssetFromPlayEngineConfig(config, assetKey) {
  if (!config?.assets || !assetKey) return;
  delete config.assets[assetKey];

  for (const comp of config.components || []) {
    for (const dep of comp.dependencies || []) {
      if (dep?.assetKey === assetKey) {
        delete dep.assetKey;
        delete dep.source;
        delete dep.sourceRef;
        delete dep.assetId;
        delete dep.isSatisfied;
      }
    }
  }

  const overrides = _.get(config, "localization.overrides") || {};
  for (const langOverrides of Object.values(overrides)) {
    for (const compOverride of Object.values(langOverrides || {})) {
      const dov = compOverride?.dependencyOverrides;
      if (!dov) continue;
      for (const [k, v] of Object.entries(dov)) {
        if (v === assetKey) delete dov[k];
      }
    }
  }
}

/** True for asset types that are stored as an image + metadata pair. */
export function isPairDepType(type) {
  return type === "atlas" || type === "bitmap-font";
}

/** Build the playEngine engine handle id for an asset key. */
export function assetIdFor(type, assetKey) {
  return `${type}:${assetKey}`;
}

/** Static folder (server route) where a given asset type's files are served. */
export function getAssetFolder(type) {
  const map = {
    image: "images",
    atlas: "atlases",
    "bitmap-font": "bitmapFonts",
    audio: "sounds",
    font: "fonts",
    video: "videos",
  };
  return map[type] || "images";
}

export const VALID_EXTS = {
  image: ["png", "jpg", "jpeg", "gif", "webp", "svg", "bmp"],
  audio: ["mp3", "ogg", "wav", "aac", "m4a", "flac"],
  font: ["ttf", "otf", "woff", "woff2"],
  video: ["mp4", "webm", "mov", "avi", "mkv"],
};

export function getExt(item) {
  return (_.get(item, "file", "") || "").split(".").pop().toLowerCase();
}

/** Resolve the company-media collection for an asset type from a props bag. */
export function getCollectionForType(collections, type) {
  switch (type) {
    case "image": return _.get(collections, "media.images", _.get(collections, "images", [])) || [];
    case "atlas": return _.get(collections, "atlases", []) || [];
    case "bitmap-font": return _.get(collections, "bitmapFonts", []) || [];
    case "audio": return _.get(collections, "sounds", []) || [];
    case "font": return _.get(collections, "fonts", []) || [];
    case "video": return _.get(collections, "videos", []) || [];
    default: return [];
  }
}

/**
 * The `accept` string for a file input picking this asset type.
 *
 * The same switch is written out in three panels; this is the copy new callers
 * should use. Kept as an explicit table rather than derived from `VALID_EXTS`
 * because the two answer different questions — `VALID_EXTS` filters media
 * records the server already has, while this one is the hint a file dialog
 * shows, and the pair types (which have no `VALID_EXTS` entry) need both halves
 * listed.
 */
export function getAcceptString(type) {
  switch (type) {
  case "image": return ".png,.jpg,.jpeg,.gif,.webp,.svg";
  case "audio": return ".mp3,.ogg,.wav,.m4a,.aac";
  case "video": return ".mp4,.webm";
  case "font": return ".ttf,.otf,.woff,.woff2";
  case "atlas": return ".json,.png,.jpg,.jpeg";
  case "bitmap-font": return ".xml,.png,.jpg,.jpeg";
  default: return "*";
  }
}

/** All playEngine asset types, in display order. */
export const ASSET_TYPES = ["image", "audio", "video", "font", "atlas", "bitmap-font"];

/** Dashboard sentinel for "no bucket assigned" (engine stores ""). */
export const NO_BUCKET = "nobucket";

/** Trim + lowercase a bucket name, mapping empty to the NO_BUCKET sentinel. */
export function normalizeBucket(value) {
  const trimmed = (value || "").trim().toLowerCase();
  return trimmed || NO_BUCKET;
}

/**
 * Collect the unique bucket names known to the company / version.
 *
 * Buckets are company-wide: the primary source is the loaded media collections
 * (already scoped to the user's company by the server). Buckets referenced by
 * the current version's components and asset map are also included so freshly
 * created (not-yet-uploaded-to) buckets still show up.
 *
 * The NO_BUCKET sentinel and empty values are always excluded from the result.
 *
 * @param {object} collections props bag ({ media:{images}, atlases, sounds, ... })
 * @param {object} [config]    playEngineConfig (components + asset map)
 * @param {object} [opts]
 * @param {string} [opts.type] restrict to a single asset type's buckets
 * @returns {string[]} sorted unique bucket names
 */
export function collectBuckets(collections, config = null, opts = {}) {
  const set = new Set();
  const add = (value) => {
    const n = normalizeBucket(value);
    if (n !== NO_BUCKET) set.add(n);
  };

  const types = opts.type ? [opts.type] : ASSET_TYPES;
  for (const type of types) {
    for (const item of getCollectionForType(collections, type)) {
      add(_.get(item, "bucket"));
    }
  }

  if (config) {
    // Component buckets aren't tied to an asset type — only include them when
    // building the unscoped (all-types) list.
    if (!opts.type) {
      for (const comp of config.components || []) add(comp.bucket);
    }
    const assets = getAssetMapFromConfig(config);
    for (const entry of Object.values(assets)) {
      if (!opts.type || entry?.type === opts.type) add(entry?.bucket);
    }
  }

  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

/**
 * Build a { type -> string[] } map of buckets available for each asset type.
 * @param {object} collections props bag
 * @param {object} [config]    playEngineConfig
 */
export function collectBucketsByType(collections, config = null) {
  const result = {};
  for (const type of ASSET_TYPES) {
    result[type] = collectBuckets(collections, config, { type });
  }
  return result;
}

/**
 * Count the pickable company media per bucket — i.e. how many records would
 * actually appear in the dropdown for that bucket. Counts match the filters used
 * by getMediaForBucketAndType (valid extension; complete pairs for atlas/bitmap-font).
 *
 * Single pass per collection so it stays cheap for large libraries.
 *
 * @param {object}   collections props bag
 * @param {string[]} buckets     bucket names to count (may include NO_BUCKET)
 * @param {object}   [opts]
 * @param {string}   [opts.type] count a single asset type; otherwise sum all types
 * @returns {Object<string, number>} bucket -> count
 */
export function countBucketsAssets(collections, buckets, opts = {}) {
  const counts = {};
  const want = new Set();
  for (const b of buckets || []) {
    const n = normalizeBucket(b);
    want.add(n);
    counts[n] = 0;
  }
  if (want.size === 0) return counts;

  const types = opts.type ? [opts.type] : ASSET_TYPES;
  for (const type of types) {
    if (isPairDepType(type)) {
      const groups = buildPairGroups(getCollectionForType(collections, type), getPairDepConfig(type));
      for (const g of groups) {
        const b = normalizeBucket(g.imageRecord?.bucket);
        if (want.has(b)) counts[b] += 1;
      }
    } else {
      const validExts = VALID_EXTS[type];
      for (const item of getCollectionForType(collections, type)) {
        const b = normalizeBucket(item.bucket);
        if (!want.has(b)) continue;
        if (validExts && !validExts.includes(getExt(item))) continue;
        counts[b] += 1;
      }
    }
  }
  return counts;
}

/** Filter a collection of media records by bucket + asset type. */
export function getMediaForBucketAndType(collections, bucket, type) {
  const collection = getCollectionForType(collections, type);
  const validExts = isPairDepType(type) ? null : VALID_EXTS[type];
  return collection.filter((item) => {
    if (_.get(item, "bucket") !== bucket) return false;
    if (validExts && !validExts.includes(getExt(item))) return false;
    return true;
  });
}

/**
 * Group paired records (atlas / bitmap-font) into { groupId, imageRecord, metaRecord }.
 */
export function buildPairGroups(records, config) {
  const groups = {};
  records.forEach((record) => {
    const set = record[config.groupField];
    if (!set) return;
    if (!groups[set]) {
      groups[set] = {
        [config.groupRecordKey]: set,
        imageRecord: null,
        [config.metaRecordKey]: null,
      };
    }
    const v = (record.variant || "").toLowerCase();
    if (v === "image") groups[set].imageRecord = record;
    else if (v === config.metaVariant) groups[set][config.metaRecordKey] = record;
  });
  return Object.values(groups).filter((g) => g.imageRecord && g[config.metaRecordKey]);
}

/** Pair-type config (atlas / bitmap-font) without dashboard-specific closures. */
export function getPairDepConfig(type) {
  if (type === "bitmap-font") {
    return {
      groupField: "fontSet",
      groupRecordKey: "fontSet",
      metaVariant: "xml",
      metaKey: "xml",
      metaRecordKey: "xmlRecord",
      metaLabel: "XML",
      metaAccept: ".xml",
      staticFolder: "bitmapFonts",
      uploadEndpoint: "bitmapFont/uploadBitmapFont",
      uploadGroupIdKey: "fontSetID",
      metaFileKey: "xmlFile",
      uploadMetaVariant: "xml",
    };
  }
  return {
    groupField: "animationSet",
    groupRecordKey: "animationSet",
    metaVariant: "json",
    metaKey: "json",
    metaRecordKey: "jsonRecord",
    metaLabel: "JSON",
    metaAccept: ".json",
    staticFolder: "atlases",
    uploadEndpoint: "atlas/uploadAtlas",
    uploadGroupIdKey: "animationSetID",
    metaFileKey: "jsonFile",
    uploadMetaVariant: "JSON",
  };
}

/**
 * Build the lightweight `@server:` source reference for DB persistence.
 *  - simple types → "@server:<mongoId>"
 *  - atlas        → { image: "@server:<imgId>", json: "@server:<jsonId>" }
 *  - bitmap-font  → { image: "@server:<imgId>", xml:  "@server:<xmlId>" }
 */
export function buildServerSourceRef(type, mongoId, pairGroup = null) {
  if (type === "atlas") {
    if (pairGroup?.imageRecord && pairGroup?.jsonRecord) {
      return {
        image: `@server:${String(pairGroup.imageRecord._id)}`,
        json: `@server:${String(pairGroup.jsonRecord._id)}`,
      };
    }
    return { image: `@server:${mongoId}`, json: null };
  }
  if (type === "bitmap-font") {
    if (pairGroup?.imageRecord && pairGroup?.xmlRecord) {
      return {
        image: `@server:${String(pairGroup.imageRecord._id)}`,
        xml: `@server:${String(pairGroup.xmlRecord._id)}`,
      };
    }
    return { image: `@server:${mongoId}`, xml: null };
  }
  return `@server:${mongoId}`;
}

/** Extract the leading mongoId from an asset entry's sourceRef (image part for pairs). */
export function mongoIdFromAssetEntry(entry) {
  if (!entry || !entry.sourceRef) return "";
  const ref = entry.sourceRef;
  if (typeof ref === "string") return ref.replace(/^@server:/, "");
  if (ref.image) return String(ref.image).replace(/^@server:/, "");
  return "";
}

/** Count entries in the asset-map (0 when missing, legacy array, or wrong shape). */
export function assetMapKeyCount(config) {
  const a = config?.assets;
  if (!a || Array.isArray(a) || typeof a !== "object") return 0;
  return Object.keys(a).length;
}

/** Normalise playEngineConfig.assets to a plain key → entry map (or {}). */
export function getAssetMapFromConfig(config) {
  const a = config?.assets;
  if (a && !Array.isArray(a) && typeof a === "object") return a;
  return {};
}

/**
 * Resolve playEngineConfig for the currently selected version.
 *
 * `setSelectedVersionPathValue` updates the matching entry in `versions[]`
 * immediately, while `selectedVersion` React state is debounced ~200 ms.
 * Prefer whichever copy has the richer asset map so the Assets tab reflects
 * migrations and saves without waiting for the debounce.
 */
export function resolvePlayEngineConfig(selectedVersion, versions) {
  const versionId = _.get(selectedVersion, "_id");
  const fromSelected = _.get(selectedVersion, "playEngineConfig") || {};
  if (!versionId) return fromSelected;

  const fromList = (versions || []).find((v) => String(v._id) === String(versionId));
  const fromListConfig = _.get(fromList, "playEngineConfig") || {};

  const selectedCount = assetMapKeyCount(fromSelected);
  const listCount = assetMapKeyCount(fromListConfig);
  if (listCount > selectedCount) return fromListConfig;
  if (selectedCount > listCount) return fromSelected;
  // Tie-break: prefer the versions[] copy (updated synchronously on edit).
  return fromList ? fromListConfig : fromSelected;
}

/** Resolve the asset key a dependency is bound to (asset-map or engine-handle id). */
export function assetKeyFromDependency(dep) {
  if (!dep) return "";
  if (dep.assetKey) return dep.assetKey;
  // Engine `get-dependencies` returns assetId like "image:<assetKey>".
  if (dep.assetId && dep.assetId !== `${dep.type}:empty`) {
    return dep.assetId.split(":").slice(1).join(":");
  }
  return "";
}

/**
 * Resolve the bound asset key for UI display. Prefer the saved playEngineConfig
 * (authoritative after rekeys/edits) over live iframe dependency state, which
 * can lag until the preview catches up.
 */
export function resolveBoundAssetKey(config, componentId, depKey, dep, language = "en") {
  if (config) {
    const isOverride = language && language !== "en";
    if (isOverride) {
      const overrideKey = _.get(
        config,
        `localization.overrides.${language}.${componentId}.dependencyOverrides.${depKey}`
      );
      if (typeof overrideKey === "string" && overrideKey) return overrideKey;
    }

    const comp = (config.components || []).find((c) => c.id === componentId);
    const savedDep = comp?.dependencies?.find((d) => d.key === depKey);
    if (savedDep?.assetKey) return savedDep.assetKey;
  }

  return assetKeyFromDependency(dep);
}

// ───────────────────────────────────────────────────────────────────────────
// Bitmap-font export size estimation
//
// An auto-generated bitmap font is exported as a subset containing only the
// characters the version actually uses. Its on-disk (compressed PNG + XML) size
// therefore scales with the number of distinct glyphs and the font size, NOT
// with the full master atlas. These helpers produce a dynamic, character-based
// estimate so the "estimated export size" updates as text changes.
// ───────────────────────────────────────────────────────────────────────────

/**
 * Tunable bytes-per-(glyph·px²) factor. Calibrate against real exports.
 * Rough model: bytes ≈ distinctChars × fontSize² × multiplier (pre-base64).
 */
export const BITMAP_FONT_SIZE_MULTIPLIER = 0.12;

/** Estimate the exported byte size of a bitmap font from its glyph count. */
export function estimateBitmapFontBytes(
  distinctCharCount,
  fontSize = 70,
  multiplier = BITMAP_FONT_SIZE_MULTIPLIER
) {
  if (!distinctCharCount || distinctCharCount <= 0) return 0;
  const size = Number(fontSize) > 0 ? Number(fontSize) : 70;
  return Math.round(distinctCharCount * size * size * multiplier);
}

function addStringCodePoints(set, text) {
  if (typeof text !== "string" || !text) return;
  for (const ch of text) set.add(ch.codePointAt(0));
}

function collectGlobalStringCodePoints(node, set, depth = 0) {
  if (node == null || depth > 8) return;
  if (typeof node === "string") return addStringCodePoints(set, node);
  if (Array.isArray(node)) {
    node.forEach((n) => collectGlobalStringCodePoints(n, set, depth + 1));
    return;
  }
  if (typeof node === "object") {
    Object.values(node).forEach((n) => collectGlobalStringCodePoints(n, set, depth + 1));
  }
}

/**
 * Count distinct code points used by every component bound to a bitmap-font
 * `assetKey`, across the given languages (defaults to all available). Only
 * characters that actually appear in the text are counted (plus space), matching
 * the export subset so the size estimate stays accurate.
 */
export function distinctUsedCharsForBitmapAsset(config, assetKey, languages = null) {
  const set = new Set();
  set.add(32);

  const langs =
    languages || _.get(config, "localization.availableLanguages", ["en"]) || ["en"];
  const overrides = _.get(config, "localization.overrides") || {};
  let usesTemplate = false;

  for (const comp of config?.components || []) {
    let bound = false;
    for (const dep of comp.dependencies || []) {
      if (dep.type !== "bitmap-font") continue;
      if (dep.assetKey === assetKey) bound = true;
      for (const lang of langs) {
        if (_.get(overrides, `${lang}.${comp.id}.dependencyOverrides.${dep.key}`) === assetKey) {
          bound = true;
        }
      }
    }
    if (!bound) continue;

    const baseText = _.get(comp, "config.text");
    if (typeof baseText === "string" && baseText.includes("{{")) usesTemplate = true;
    addStringCodePoints(set, baseText);

    for (const lang of langs) {
      const t = _.get(overrides, `${lang}.${comp.id}.configOverrides.text`);
      if (typeof t === "string" && t.includes("{{")) usesTemplate = true;
      addStringCodePoints(set, t);
    }
  }

  if (usesTemplate) collectGlobalStringCodePoints(config?.globalVariables, set);
  return set.size;
}

/**
 * Estimate total exported bytes for all auto-generated bitmap fonts in a
 * playEngineConfig, based on the characters actually used.
 *
 * @param {object} config playEngineConfig
 * @param {object} [opts]
 * @param {number} [opts.multiplier]
 * @param {string[]} [opts.languages]
 * @param {(assetKey:string, entry:object)=>number} [opts.fontSizeFor] resolve a per-asset font size
 * @returns {{ total:number, perAsset:Record<string,{chars:number,bytes:number,fontSize:number}> }}
 */
export function estimatePlayEngineBitmapFontsBytes(config, opts = {}) {
  const result = { total: 0, perAsset: {} };
  const assets = getAssetMapFromConfig(config);
  for (const [assetKey, entry] of Object.entries(assets)) {
    if (!entry || entry.type !== "bitmap-font") continue;
    const chars = distinctUsedCharsForBitmapAsset(config, assetKey, opts.languages || null);
    const fontSize = opts.fontSizeFor ? Number(opts.fontSizeFor(assetKey, entry)) || 70 : 70;
    const bytes = estimateBitmapFontBytes(chars, fontSize, opts.multiplier);
    result.perAsset[assetKey] = { chars, bytes, fontSize };
    result.total += bytes;
  }
  return result;
}

/** Convert a server-stored asset file to a base64 data-URL. */
export async function assetFileToBase64(filename, type) {
  const folder = getAssetFolder(type);
  const resp = await fetch(`${folder}/${filename}`);
  const blob = await resp.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/** Convert a local File object to a base64 data-URL. */
export function localFileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/** Normalise any data URL to the application/json MIME the atlas loader needs. */
export function rewriteAsJsonDataUrl(dataUrl) {
  if (!dataUrl) return dataUrl;
  if (dataUrl.startsWith("data:application/json;base64,")) return dataUrl;
  const commaIdx = dataUrl.indexOf(",");
  if (commaIdx === -1) return dataUrl;
  return `data:application/json;base64,${dataUrl.slice(commaIdx + 1)}`;
}

/**
 * Upload a vector font (TTF/OTF/WOFF) and auto-generate a bitmap font (PNG + XML).
 * Used by Version Settings and the Assets tab.
 *
 * @param {File} fontFile
 * @param {object} [opts]
 * @param {string} [opts.bucket]
 * @param {function} [opts.onFontUploaded] — called with font collection records after upload
 * @returns {Promise<{ fontRecords, records, fontSetID, pairGroup, preview, warnings, pairConfig }>}
 */
export async function uploadFontAndGenerateBitmapFont(fontFile, { bucket = "nobucket", onFontUploaded } = {}) {
  const accessToken = localStorage.getItem("session.accessToken");
  const fontBaseName = fontFile.name.replace(/\.[^/.]+$/, "");

  const fontForm = new FormData();
  fontForm.append("files", fontFile);
  fontForm.append("componentType", null);
  fontForm.append("bucket", bucket);
  fontForm.append("accessToken", accessToken);
  fontForm.append("uploadedFileName", fontBaseName);
  fontForm.append("fontFamily", fontBaseName);

  const fontResp = await fetch(getServerUri("font/uploadFont"), { method: "POST", body: fontForm });
  if (fontResp.status === 401) {
    const err = new Error("You are not authorized to upload.");
    err.status = 401;
    throw err;
  }
  if (fontResp.status !== 200) {
    throw new Error(`Font upload failed (status ${fontResp.status})`);
  }
  const fontResult = await fontResp.json();
  const fontRecords = _.get(fontResult, "records", []);
  if (!fontRecords.length) throw new Error("No font record returned from upload");
  if (typeof onFontUploaded === "function") onFontUploaded(fontRecords);

  const fontId = String(fontRecords[0]._id);
  const genResp = await fetch(getServerUri("bitmapFont/generateFromFont"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ fontId, bucket, accessToken }),
  });
  if (genResp.status !== 200) {
    throw new Error(`Bitmap generation failed (status ${genResp.status})`);
  }
  const genResult = await genResp.json();
  const records = _.get(genResult, "records", []);
  if (records.length < 2) throw new Error("Expected image + xml bitmap-font records");

  const pairConfig = getPairDepConfig("bitmap-font");
  const imageRecord = records.find((r) => (r.variant || "").toLowerCase() === "image");
  const xmlRecord = records.find((r) => (r.variant || "").toLowerCase() === "xml");
  const pairGroup = {
    [pairConfig.groupRecordKey]: _.get(genResult, "fontSetID"),
    imageRecord,
    [pairConfig.metaRecordKey]: xmlRecord,
  };
  const preview = {
    image: _.get(genResult, "preview.image"),
    [pairConfig.metaKey]: _.get(genResult, "preview.xml"),
  };

  return {
    fontRecords,
    records,
    fontSetID: _.get(genResult, "fontSetID"),
    pairGroup,
    preview,
    warnings: _.get(genResult, "warnings", []) || [],
    pairConfig,
  };
}

/**
 * Resolve loadable bytes for an asset-map entry (iframe handle, then @server media).
 * Used for live preview when wiring a dependency to an existing asset key.
 */
export async function resolveAssetEntryLiveSource(collections, iFrameId, assetKey, type, entry, sendCommandFn) {
  if (iFrameId && sendCommandFn) {
    try {
      const resp = await sendCommandFn(iFrameId, { type: "playengine:get-assets" }, 5000);
      const loaded = resp?.assets?.[assetKey];
      if (loaded?.source) return loaded.source;
    } catch (err) {
      console.warn(`[resolveAssetEntryLiveSource] get-assets failed: ${err.message}`);
    }
  }

  const mongoId = mongoIdFromAssetEntry(entry);
  if (!mongoId) return null;

  const media = getMediaForBucketAndType(collections, entry.bucket || "nobucket", type);
  if (isPairDepType(type)) {
    const cfg = getPairDepConfig(type);
    const groups = buildPairGroups(media, cfg);
    const group =
      groups.find((g) => g[cfg.groupRecordKey] === mongoId) ||
      groups.find((g) => String(g.imageRecord?._id) === mongoId);
    if (!group) return null;
    const [imageB64, rawMetaB64] = await Promise.all([
      assetFileToBase64(group.imageRecord.file, type),
      assetFileToBase64(group[cfg.metaRecordKey].file, type),
    ]);
    const metaVal = type === "atlas" ? rewriteAsJsonDataUrl(rawMetaB64) : rawMetaB64;
    return { image: imageB64, [cfg.metaKey]: metaVal };
  }

  const record = media.find((m) => String(m._id) === mongoId);
  if (record) return assetFileToBase64(record.file, type);
  return null;
}
